import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { api, TValidationResults } from '@verifime/api-definition';
import {
  ConfirmAndCancel,
  DisplayAlertErrors,
  GenerateFormFields_New,
  getFieldsRules_New,
  RenderType,
  StepWrapper,
  TFormFieldsAndRules_New,
  TStepContent,
  TUploadFile,
  useCustomForm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import {
  DocumentType,
  EntityStatus,
  SERVER_ERRORS,
  stringUtils,
  TDocument,
  validationRules,
} from '@verifime/utils';
import { TIdentityDocumentsUploadConfig } from '@verifime/views';
import { format } from 'date-fns';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EntityDocumentUploader } from './EntityDocumentUploader';
import { determineMode, TDisplayMode, TSwitchModeConfig } from './modeDeterminer';
import {
  OcrEditor,
  SUPPORT_OCR_DOCUMENT_TYPES,
  TOcrData,
  TSupportedOcrDocumentType,
} from './OcrEditor';

type DocumentInstructionsType = {
  [DocumentType.Passport]: string;
  [DocumentType.DriverLicence]: {
    main: string;
    note: string;
  };
  [DocumentType.MedicareCard]: string;
  [DocumentType.OtherIdentityDocument]: string;
};

const DOCUMENT_INSTRUCTIONS: DocumentInstructionsType = {
  [DocumentType.Passport]:
    'Upload a photo of the first page of your passport showing your details clearly.',
  [DocumentType.DriverLicence]: {
    main: "Upload photos of the FRONT AND BACK of your driver's licence. Make sure your photo and card number are clearly visible.",
    note: 'If you are using the Service NSW app, you may need to scroll down to be able to see your card number. Take two screenshots in this case to capture both your photo and card number.',
  },
  [DocumentType.MedicareCard]:
    'Upload a photo of the FRONT of your Medicare Card showing your details clearly.',
  [DocumentType.OtherIdentityDocument]:
    'eg. a government-issued national ID card. Upload a photo of the document showing your details clearly.',
} as const;

type TEntityDocumentsAndOcrProps = TStepContent & {
  verificationMethodStep: number;
  onSuccess?: () => void;
  config?: TIdentityDocumentsUploadConfig;
};

const DocumentInstructions = ({ documentType }: { documentType: DocumentType }) => {
  if (!(documentType in DOCUMENT_INSTRUCTIONS)) {
    return null;
  }

  if (documentType === DocumentType.DriverLicence) {
    return (
      <>
        <Typography variant="body1">
          {DOCUMENT_INSTRUCTIONS[DocumentType.DriverLicence].main}
        </Typography>
        <Typography variant="body1">
          <b>Note:</b> {DOCUMENT_INSTRUCTIONS[DocumentType.DriverLicence].note}
        </Typography>
      </>
    );
  }

  const instructions = DOCUMENT_INSTRUCTIONS[documentType as keyof DocumentInstructionsType];
  return typeof instructions === 'string' ? (
    <Typography variant="body1">{instructions}</Typography>
  ) : null;
};

const Header = () => (
  <Box>
    <Typography variant="h5">Add verification Documents</Typography>
    <Typography variant="subtitle1">Upload valid ID(s) to verify your identity</Typography>
  </Box>
);

const NO_INITIAL_NUMBER_OF_FILES_UPLOADED = -1;

export const EntityDocumentsAndOcr = ({
  onBack,
  onNext,
  step,
  numSteps,
  verificationMethodStep,
  memoizedObj,
  setMemoizedObj,
  onSuccess,
  config: { documentTypeChoices, documentCount },
}: TEntityDocumentsAndOcrProps) => {
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);
  const [numUploadedFiles, setNumUploadedFiles] = useState(NO_INITIAL_NUMBER_OF_FILES_UPLOADED);
  const [continueState, setContinueState] = useState<
    'NONE' | 'SKIP_UPLOAD' | 'FINISH_UPLOAD' | 'RENDER_FORM' | 'FINISH_FORM'
  >('NONE');
  const [isRetriving, setIsRetriving] = useState(false);
  const formDataRef = useRef<TOcrData | null>(null);
  const [validationResults, setValidationResults] = useState<TValidationResults>([]);
  const [noEnoughFilesUploadedMessage, setNoEnoughFilesUploadedMessage] =
    useState<ReactNode>(undefined);
  const [uploadRequired, setUploadRequired] = useState(true);

  const [displayMode, setDisplayMode] = useState<TDisplayMode>('fileUploaderThenFormEditor');
  const [switchMode, setSwitchMode] = useState<TSwitchModeConfig | undefined>(undefined);
  const [userSwitchedDisplayMode, setUserSwitchedDisplayMode] = useState<TDisplayMode | null>(null);

  const modeDeterminer = useCallback(() => {
    determineMode({
      customer: memoizedObj.customer,
      documentType: selectedDocumentType,
      documentTypeChoices,
    }).then(({ displayMode, switchMode, uploadRequired }) => {
      if (userSwitchedDisplayMode) {
        setDisplayMode(userSwitchedDisplayMode);
      } else {
        setDisplayMode(displayMode);
      }
      setSwitchMode(switchMode);
      setUploadRequired(uploadRequired);
    });
  }, [memoizedObj.customer, selectedDocumentType, documentTypeChoices, userSwitchedDisplayMode]);

  useEffect(() => {
    modeDeterminer();
  }, [modeDeterminer]);

  const supportedDocumentTypes = useMemo(() => {
    const documentTypes = documentTypeChoices.map(({ type: { code } }) => code);
    const availableVerificationMethods = (memoizedObj.verificationMethods ?? [])
      .map(
        (verificationMethod: DocumentType) =>
          documentTypes.includes(verificationMethod) && verificationMethod,
      )
      .filter(Boolean);
    // Update to latest avaialble verification methods once memoized verification methods changes
    if (availableVerificationMethods.length !== memoizedObj.verificationMethods?.length) {
      setMemoizedObj((prev) => ({
        ...prev,
        verificationMethods: availableVerificationMethods,
      }));
    }

    // For subsequent steps, filter out previously selected document types
    // Get all verification methods from previous steps
    let previousStepsMethods =
      availableVerificationMethods.length > 0
        ? availableVerificationMethods.slice(0, verificationMethodStep - 1).filter(Boolean)
        : [];

    // For the first step, show all available document types
    if (verificationMethodStep === 1) {
      previousStepsMethods = [];
    }

    // Use selectedDocumentTypes to filter out previously selected document types
    const filteredTypes = getDocumentTypesExcludeSelectedOnes({
      selectedDocumentTypes: previousStepsMethods,
      documentTypeChoices,
    });
    return filteredTypes.sort((a, b) => {
      if (a.code === 'DriverLicence' && b.code !== 'DriverLicence') return -1; // TODO: VME-3413 temporarily put DriverLicence on top. We will implement the sort order by backend requirement API later.
      if (b.code === 'DriverLicence' && a.code !== 'DriverLicence') return 1;
      return 0;
    });
  }, [memoizedObj.verificationMethods, verificationMethodStep, documentTypeChoices]);

  const fieldName = `verificationMethod${verificationMethodStep}`;

  const formFieldsAndRules: TFormFieldsAndRules_New = {
    row1: [
      {
        label: 'Select Document Type',
        fieldName,
        renderType: RenderType.Select,
        rules: validationRules.REQUIRED_STRING_RULES,
        items: supportedDocumentTypes,
      },
    ],
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useCustomForm({ schema: getFieldsRules_New(formFieldsAndRules) });

  const initializeVerificationMethods = useCallback(() => {
    const defaultDocumentTypes = getDefaultDocumentTypes(supportedDocumentTypes, documentCount);
    setMemoizedObj((prev) => ({
      ...prev,
      verificationMethods: defaultDocumentTypes,
    }));
  }, [setMemoizedObj, supportedDocumentTypes]);

  useEffect(() => {
    if (!memoizedObj.verificationMethods) {
      setIsRetriving(true);

      api
        .getV1personIdverificationDocuments({ params: { id: memoizedObj.customer.id } })
        .then((docs) => {
          // Get unique document types from API response
          const uniqueDocTypes = [...new Set(docs.map((doc) => doc.docType))];

          // Reorder document types to match documentTypeChoices order,
          // then filter out document types that are not available
          const existingDocumentTypes = documentTypeChoices
            .map((choice) => choice.type.code)
            .filter((code) => uniqueDocTypes.includes(code));

          if (existingDocumentTypes.length > 0) {
            // Truncate existingDocumentTypes to match documentCount if needed
            const truncatedTypes = existingDocumentTypes.slice(0, documentCount);
            setMemoizedObj((prev) => ({
              ...prev,
              verificationMethods: truncatedTypes,
              retrievedVerificationMethods: existingDocumentTypes,
            }));
          } else {
            const defaultDocumentTypes = getDefaultDocumentTypes(
              supportedDocumentTypes,
              documentCount,
            );
            setMemoizedObj((prev) => ({
              ...prev,
              verificationMethods: defaultDocumentTypes,
              retrievedVerificationMethods: existingDocumentTypes,
            }));
          }
        })
        .finally(() => {
          setIsRetriving(false);
        });
    }
  }, [
    initializeVerificationMethods,
    memoizedObj.customer.id,
    memoizedObj.verificationMethods,
    setMemoizedObj,
    supportedDocumentTypes,
  ]);

  useEffect(() => {
    if (!memoizedObj.verificationMethods?.length) {
      initializeVerificationMethods();
      return;
    }
    // Get the document type for the current step
    const currentDocumentType = memoizedObj.verificationMethods[verificationMethodStep - 1];

    // If currentDocumentType is not available, set a default based on supported types
    if (!currentDocumentType) {
      const filteredTypes =
        verificationMethodStep === 1
          ? supportedDocumentTypes
          : getDocumentTypesExcludeSelectedOnes({
              // Pass all previously selected document types as an array
              selectedDocumentTypes: memoizedObj.verificationMethods
                .slice(0, verificationMethodStep - 1)
                .filter(Boolean),
              documentTypeChoices: documentTypeChoices,
            });
      const defaultDocumentType = filteredTypes[0]?.code as DocumentType;

      // Update memoizedObj with the default doc type
      setMemoizedObj((prev) => {
        // Create a copy of the current verification methods or initialize an empty array
        const updatedMethods = [...(prev.verificationMethods || [])];

        // Set the document type for the current step
        updatedMethods[verificationMethodStep - 1] = defaultDocumentType;

        return {
          ...prev,
          verificationMethods: updatedMethods,
        };
      });

      setSelectedDocumentType(defaultDocumentType);
      setValue(fieldName, defaultDocumentType);
    } else {
      setSelectedDocumentType(currentDocumentType as DocumentType);
      setValue(fieldName, currentDocumentType);
    }
  }, [
    fieldName,
    initializeVerificationMethods,
    memoizedObj.verificationMethods,
    setValue,
    verificationMethodStep,
    supportedDocumentTypes,
    setMemoizedObj,
    documentTypeChoices,
  ]);

  const handleDocumentTypeChanges = (documentType: DocumentType) => {
    // Get current verification methods
    const currentMethods = [...(memoizedObj.verificationMethods ?? [])];

    // Update the current step's document type
    currentMethods[verificationMethodStep - 1] = documentType;

    // Ensure we don't have duplicate document types when possible
    // If the selected type is already used in another step, try to swap them
    for (let i = 0; i < currentMethods.length; i++) {
      // Skip the current step
      if (i === verificationMethodStep - 1) continue;

      // If we found a duplicate, try to replace it with a different type
      if (currentMethods[i] === documentType) {
        // Find an unused document type
        const unusedType = supportedDocumentTypes.find(
          (type) => !currentMethods.includes(type.code as DocumentType),
        );

        if (unusedType) {
          // Replace the duplicate with an unused type
          currentMethods[i] = unusedType.code as DocumentType;
        }
        // If no unused type is available, we keep the duplicate
      }
    }

    setMemoizedObj((prev) => ({
      ...prev,
      verificationMethods: currentMethods,
    }));

    setSelectedDocumentType(documentType);
    setNumUploadedFiles(NO_INITIAL_NUMBER_OF_FILES_UPLOADED);
    setUploadRequired(true);
    setUserSwitchedDisplayMode(null);
  };

  const handleOcrFormChange = useCallback(
    (isValid: boolean, data: TOcrData) => {
      formDataRef.current = data;

      // If no files are uploaded, we don't need to validate the form
      // and ignore numUploadedFiles === -1, because it means the form is not yet initialized
      if (numUploadedFiles === 0 && displayMode !== 'fileUploaderAndFormEditor') {
        setValidationResults([]);
        setContinueState('NONE');
        return;
      }

      if (isValid) {
        setValidationResults([]);
        setContinueState('FINISH_FORM');
      } else {
        setContinueState('RENDER_FORM');
      }
    },
    [memoizedObj.customer, displayMode, numUploadedFiles],
  );

  const handleUploadLimitReached = useCallback(
    ({ uploadedFiles }: { uploadedFiles: any[] }) => {
      setNoEnoughFilesUploadedMessage(undefined);
      if (uploadedFiles.length < 1) {
        if (uploadRequired) {
          setContinueState('NONE');
          formDataRef.current = null;
        }

        setNumUploadedFiles(0);

        return;
      }

      // We don't support ocr for OtherIdentityDocument right now,
      // thus allow `continue` directly
      if (selectedDocumentType === DocumentType.OtherIdentityDocument) {
        setContinueState('SKIP_UPLOAD');
      } else {
        // Handle race conditions update
        setContinueState('FINISH_UPLOAD');
        setNumUploadedFiles(uploadedFiles.length);
      }
    },
    [selectedDocumentType, documentTypeChoices, displayMode],
  );

  const handleContinue = async () => {
    const currentCustomer = { ...memoizedObj.customer };
    if (verificationMethodStep === documentCount) {
      let verificationMethods = memoizedObj.verificationMethods as TDocument['code'][];

      const nonUsedDocumentTypes = documentTypeChoices.filter(
        (documentType) => !verificationMethods.includes(documentType.type.code),
      );

      // Delete unused document data from current customer
      nonUsedDocumentTypes.forEach((documentType) => {
        const documentFieldName = stringUtils.lowerCaseFirstLetter(documentType.type.code);
        delete currentCustomer[documentFieldName];
      });

      // Ask to delete unused document (keep selected ones)
      api.putV1personIdverificationDocuments(verificationMethods, {
        params: { id: memoizedObj.customer.id },
      });
    }

    // Customers only needs to upload OtherIdentityDocument, Admin will do the rest.
    if (selectedDocumentType === DocumentType.OtherIdentityDocument) {
      onSuccess?.();
      onNext();
      return;
    }

    const documentFieldName = stringUtils.lowerCaseFirstLetter(selectedDocumentType);
    let documentData = {} as TOcrData;
    // TODO: Further code clean
    if (selectedDocumentType === DocumentType.Passport) {
      documentData = formDataRef.current.passport;
      documentData.passportStatus = 'InProgress';
      documentData.passportExpiry = format(
        new Date(documentData.passportExpiry as Date),
        'yyyy-MM-dd',
      );
    }
    if (selectedDocumentType === DocumentType.DriverLicence) {
      documentData = formDataRef.current.driverLicence;
      documentData.licenceStatus = 'InProgress';
      documentData.licenceExpiry = format(
        new Date(documentData.licenceExpiry as Date),
        'yyyy-MM-dd',
      );
    }
    if (selectedDocumentType === DocumentType.MedicareCard) {
      documentData = formDataRef.current.medicareCard;
      documentData.medicareStatus = 'InProgress';
      documentData.medicareCardExpiry = format(
        new Date(documentData.medicareCardExpiry as Date),
        'yyyy-MM-dd',
      );
    }

    const person = { ...currentCustomer, [documentFieldName]: documentData };

    api
      .putV2personId(
        { entityStatus: EntityStatus.Draft, person },
        { params: { id: memoizedObj.customer.id } },
      )
      .then((res) => {
        setMemoizedObj((prev) => ({
          ...prev,
          customer: res.customer,
        }));
        onSuccess?.();
        onNext();
      })
      .catch(
        (error: {
          validationResults?: TValidationResults;
          response?: { data?: { validationResults?: TValidationResults } };
          message?: string;
        }) => {
          setContinueState('FINISH_FORM');
          const validationResults =
            error['validationResults'] ||
            (error.response?.data?.['validationResults'] as TValidationResults);
          if (validationResults?.length > 0) {
            setValidationResults(validationResults);
            return;
          }

          setValidationResults([
            {
              path: '',
              name: '',
              resultType: 'Error',
              resultScope: 'Form',
              message: SERVER_ERRORS.default,
            },
          ]);
        },
      );
  };

  const disabledContinue = !['SKIP_UPLOAD', 'FINISH_FORM'].includes(continueState);
  const filterOutedSupportOcrDocumentTypes = SUPPORT_OCR_DOCUMENT_TYPES.filter(
    (type) => type !== 'OtherIdentityDocument',
  ) as DocumentType[];

  const shouldShowOcrEditor = () => {
    // Don't show OCR editor if document type is not supported or not selected
    if (
      !selectedDocumentType ||
      !filterOutedSupportOcrDocumentTypes.includes(selectedDocumentType)
    ) {
      return false;
    }

    // In fileUploaderAndFormEditor mode, always show the OCR editor
    if (displayMode === 'fileUploaderAndFormEditor') {
      return true;
    }

    // In fileUploaderThenFormEditor mode, only show OCR editor after files are uploaded
    if (displayMode === 'fileUploaderThenFormEditor') {
      return (
        continueState === 'FINISH_UPLOAD' ||
        continueState === 'RENDER_FORM' ||
        continueState === 'FINISH_FORM'
      );
    }

    return false;
  };

  /**
   * TODO: Needs to handle the situation that the driver's licence has more than one uploaded files,
   * and only delete selected document data from current curstomer when all files are removed
   */
  const handleFileRemove = (file: TUploadFile, uploadedFiles: TUploadFile[]) => {
    if (uploadedFiles.length > 0) {
      return;
    }

    setContinueState('NONE');
    setValidationResults([]);
    modeDeterminer();
    const toBeUpdatedMeoizedObj = { ...memoizedObj };
    const documentFieldName = stringUtils.lowerCaseFirstLetter(selectedDocumentType);
    // No such document data in the customer, no need to call api to delete the data
    if (!(documentFieldName in toBeUpdatedMeoizedObj.customer)) {
      return;
    }
    delete toBeUpdatedMeoizedObj.customer[documentFieldName];
    api
      .putV2personId(
        { entityStatus: EntityStatus.Draft, person: toBeUpdatedMeoizedObj.customer },
        { params: { id: memoizedObj.customer.id } },
      )
      .then((res) => {
        setMemoizedObj((prev) => ({
          ...prev,
          customer: res.customer,
        }));
      })
      .catch((error) => console.error(error));
  };

  return (
    <StepWrapper
      header={<Header />}
      content={
        <Stack gap={tokens.spacingLg}>
          {selectedDocumentType && <DocumentInstructions documentType={selectedDocumentType} />}

          {isRetriving ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            selectedDocumentType && (
              <Stack key={selectedDocumentType} gap={tokens.spacingBase}>
                <GenerateFormFields_New
                  formFieldsAndRules={formFieldsAndRules}
                  control={control}
                  errors={errors}
                  fieldsOperations={{
                    [fieldName]: {
                      onOptionChange: handleDocumentTypeChanges,
                      initValue: selectedDocumentType,
                    },
                  }}
                />
                {noEnoughFilesUploadedMessage && (
                  <DisplayAlertErrors errors={[{ message: noEnoughFilesUploadedMessage }]} />
                )}

                <EntityDocumentUploader
                  multiple={selectedDocumentType === DocumentType.DriverLicence}
                  maxFileCount={selectedDocumentType === DocumentType.DriverLicence ? 2 : 1}
                  entityId={memoizedObj.customer.id}
                  documentType={selectedDocumentType}
                  onLimitReached={handleUploadLimitReached}
                  onFileRemove={handleFileRemove}
                />

                {switchMode && displayMode !== 'fileUploaderAndFormEditor' && (
                  <Box>
                    <Button
                      variant="text"
                      onClick={() => {
                        if (!switchMode.to) {
                          return;
                        }
                        const docTypeConfig = documentTypeChoices.find(
                          (choice) => choice.type.code === selectedDocumentType,
                        );
                        setUserSwitchedDisplayMode(switchMode.to);
                        setUploadRequired(docTypeConfig?.rules?.uploadRequired);
                        setDisplayMode(switchMode.to);
                        // Hide switch mode
                        setTimeout(() => {
                          setSwitchMode(undefined);
                        }, 300);
                      }}
                    >
                      {switchMode.props.message}
                    </Button>
                  </Box>
                )}

                {shouldShowOcrEditor() && (
                  <OcrEditor
                    customer={memoizedObj.customer}
                    documentType={selectedDocumentType as TSupportedOcrDocumentType}
                    onFormChange={handleOcrFormChange}
                    uploadRequired={uploadRequired}
                    numUploadedFiles={numUploadedFiles}
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                    validationResults={validationResults}
                  />
                )}
              </Stack>
            )
          )}
        </Stack>
      }
      footer={
        <ConfirmAndCancel
          cancel={
            <Button fullWidth variant="outlined" size="large" onClick={onBack}>
              Back
            </Button>
          }
          confirm={
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={disabledContinue}
              onClick={handleContinue}
            >
              Continue
            </Button>
          }
        />
      }
      step={step}
      numSteps={numSteps}
      memoizedObj={memoizedObj}
      setMemoizedObj={setMemoizedObj}
    />
  );
};

function getDocumentTypesExcludeSelectedOnes({
  selectedDocumentTypes,
  documentTypeChoices,
}: {
  selectedDocumentTypes?: DocumentType[];
  documentTypeChoices?: TIdentityDocumentsUploadConfig['documentTypeChoices'];
}): TDocument[] {
  if (!documentTypeChoices || documentTypeChoices.length === 0) {
    throw new Error('Document type choices must be provided');
  }

  // Convert document type choices to TDocument array
  let documentTypes = documentTypeChoices.map(({ type: { code, label } }) => ({
    code,
    label,
  }));

  // If we have an array of document types to exclude, filter them all out
  if (selectedDocumentTypes && selectedDocumentTypes.length > 0) {
    return documentTypes.filter(
      (item) => !selectedDocumentTypes.includes(item.code as DocumentType),
    );
  }
  return documentTypes;
}

function getDefaultDocumentTypes(
  supportedDocumentTypes: TDocument[],
  documentCount: number,
): DocumentType[] {
  // Return an array of document types with length matching config.documentCount
  if (supportedDocumentTypes.length === 0) {
    return [];
  }

  // When documentCount is 1, only return the first document type
  if (documentCount === 1) {
    return [supportedDocumentTypes[0].code as DocumentType];
  }

  // Always use the first document type for the first step
  const defaultTypes: DocumentType[] = [supportedDocumentTypes[0].code as DocumentType];

  // For each additional step, try to find a unique document type
  // If not enough unique types are available, reuse existing types
  const usedTypes = new Set([defaultTypes[0]]);

  // Add remaining document types, trying to avoid duplicates when possible
  for (let i = 1; i < documentCount && i < supportedDocumentTypes.length; i++) {
    // Find a document type that hasn't been used yet
    const availableType = supportedDocumentTypes.find(
      (type) => !usedTypes.has(type.code as DocumentType),
    );

    if (availableType) {
      defaultTypes.push(availableType.code as DocumentType);
      usedTypes.add(availableType.code as DocumentType);
    } else {
      // If all types have been used, start reusing from the beginning
      defaultTypes.push(
        supportedDocumentTypes[i % supportedDocumentTypes.length].code as DocumentType,
      );
    }

    return defaultTypes;
  }
}
